import React, { useState, useEffect } from 'react';
import { 
    Box, 
    List, 
    ListItem, 
    ListItemAvatar, 
    ListItemText, 
    Avatar, 
    Typography, 
    Divider, 
    Badge,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import { 
    SmartToy as RobotIcon, 
    Person as PersonIcon, 
    Message as MessageIcon,
    Send as SendIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useMessages } from '../../context/MessageContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import { parseUtc } from '../../utils/time';


const MessagesHub = () => {
    const { conversations, loading, fetchConversations } = useMessages();
    const { user, effectiveRole } = useAuth();
    const navigate = useNavigate();
    const isWorker = (effectiveRole || user?.role) === 'worker';
    const [newMsgOpen, setNewMsgOpen] = useState(false);
    const [msgContent, setMsgContent] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    const getIcon = (type) => {
        switch (type) {
            case 'manager_ai_private':
            case 'worker_ai':
            case 'client_ai':
                return <RobotIcon />;
            default:
                return <PersonIcon />;
        }
    };

    const getAvatarColor = (type) => {
        switch (type) {
            case 'manager_ai_private':
            case 'worker_ai':
            case 'client_ai':
                return '#3B82F6';
            case 'manager_worker':
                return '#10B981';
            default:
                return '#94A3B8';
        }
    };

    const handleSendToManager = async () => {
        if (!msgContent.trim()) return;
        setSending(true);
        try {
            await api.post('/messages/send', {
                channel_type: 'manager_worker',
                recipient_type: 'manager',
                recipient_id: null,
                content: msgContent
            });
            setMsgContent('');
            setNewMsgOpen(false);
            fetchConversations();
        } catch (err) {
            console.error('Failed to send message to manager:', err);
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (conversations.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
                <MessageIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6">No conversations yet</Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>Messages with AI and team members will appear here.</Typography>
                {isWorker && (
                    <Button variant="contained" startIcon={<SendIcon />} onClick={() => setNewMsgOpen(true)}>
                        Message Manager
                    </Button>
                )}
                {renderNewMsgDialog()}
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: 'white', minHeight: '100%', borderRadius: 2, overflow: 'hidden' }}>
            {isWorker && (
                <Box sx={{ p: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Button variant="outlined" fullWidth startIcon={<SendIcon />} onClick={() => setNewMsgOpen(true)}>
                        Message Manager
                    </Button>
                </Box>
            )}
            <List sx={{ p: 0 }}>
                {conversations.map((conv, index) => (
                    <React.Fragment key={`${conv.channel_type}-${conv.participant_id || 'self'}`}>
                        <ListItem 
                            button 
                            onClick={() => {
                                const participant = conv.participant_id || 'self';
                                const basePath = isWorker ? '/worker/messages' : '/messages';
                                navigate(`${basePath}/${conv.channel_type}/${participant}${conv.job_id ? `?job_id=${conv.job_id}` : ''}`);
                            }}
                            sx={{ py: 2 }}
                        >
                            <ListItemAvatar>
                                <Badge 
                                    color="primary" 
                                    badgeContent={conv.unread_count} 
                                    invisible={conv.unread_count === 0}
                                >
                                    <Avatar sx={{ bgcolor: getAvatarColor(conv.channel_type) }}>
                                        {getIcon(conv.channel_type)}
                                    </Avatar>
                                </Badge>
                            </ListItemAvatar>
                            <ListItemText 
                                primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: conv.unread_count > 0 ? 700 : 500 }}>
                                            {conv.participant_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDistanceToNow(parseUtc(conv.last_message_at), { addSuffix: true })}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        noWrap 
                                        sx={{ fontWeight: conv.unread_count > 0 ? 600 : 400 }}
                                    >
                                        {conv.last_message}
                                    </Typography>
                                }
                            />
                        </ListItem>
                        {index < conversations.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                ))}
            </List>
            {renderNewMsgDialog()}
        </Box>
    );

    function renderNewMsgDialog() {
        return (
            <Dialog open={newMsgOpen} onClose={() => setNewMsgOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700 }}>Message Manager</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        autoFocus
                        placeholder="Type your message..."
                        value={msgContent}
                        onChange={(e) => setMsgContent(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setNewMsgOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSendToManager}
                        disabled={!msgContent.trim() || sending}
                        sx={{ fontWeight: 600 }}
                    >
                        {sending ? <CircularProgress size={24} /> : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
};

export default MessagesHub;
